import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import {
	TFileId,
	ICreateModifierValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TLanguageIDs,
	IDefaultLanguage,
	IGetModifierInfo,
	IUpdateModifier,
	ITotalPrice,
} from 'types';
import { editModifierReducer } from './editModifierReducer';
import {
	EditModifierContext,
	editModifierInitialState,
	TEditModifierVisibleForm,
	TTypeOfEditModifierModalWindow,
} from './editModifierContext';
import { EditModifierActionTypes } from './editModifierActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getModifierInfo } from 'backend/modifiers';

const EditModifierState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editModifierReducer, editModifierInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const modifierID = params.get('modifierID');

	const { response, loading, refetch } = useFetchData<IGetModifierInfo, string>(
		modifierID ? getModifierInfo : null,
		modifierID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditModifierActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.modifier.name);

	useEffect(() => {
		if (response) {
			const quantityMaxLimitNumbers = response.data.modifier.restaurantsModifiers
				.map((el) => el.quantityMaxLimit)
				.filter((el) => el);
			const maxNumber = quantityMaxLimitNumbers.length
				? Math.max(...(quantityMaxLimitNumbers as number[]))
				: null;

			dispatch({
				type: EditModifierActionTypes.SET_ALL_DATA,
				payload: {
					modifierId: response.data.modifier.id,
					filePath: response.data.modifier.filePath,
					preSignedUrl: response.data.modifier.preSignedUrl,
					selectedCustomer: response.data.modifier.customerId,
					restaurantsForPrice: response.data.modifier.restaurantsModifiers.map((el) => el.restaurantId),
					totalPrices: response.data.modifier.restaurantsModifiers.map((el) => ({
						restaurantId: el.restaurantId,
						restaurantName: el.restaurantName,
						restaurantStatus: el.restaurantStatus,
						price: String(el.price),
					})),
					IDsIsForceDisabledRestaurant: response.data.modifier.restaurantsModifiers
						.filter((el) => el.forceDisable)
						.map((el) => el.restaurantId),
					name: response.data.modifier.name,
					articleNumber: response.data.modifier.articleNumber,
					description: response.data.modifier.description,
					externalId: response.data.modifier.externalId || '',
					selectedModifierGroups: response.data.modifier.modifierGroups.map((el) => el.id),
					isDisabledInRestaurants: response.data.modifier.restaurantsModifiers
						.filter((el) => el.isDisabled)
						.map((el) => el.restaurantId),
					isRestrictedInRestaurants: response.data.modifier.restaurantsModifiers
						.filter((el) => el.isRestricted)
						.map((el) => el.restaurantId),
					isPreselectedInRestaurants: response.data.modifier.restaurantsModifiers
						.filter((el) => el.isPreselected)
						.map((el) => el.restaurantId),
					quantityRegulationRestaurants: response.data.modifier.restaurantsModifiers
						.filter((el) => el.quantityMaxLimit)
						.map((el) => el.restaurantId),
					quantityMaxLimit: maxNumber,
					activationSchedule: response.data.modifier.activationCron,
					deactivationSchedule: response.data.modifier.deactivationCron,
					translations: response.data.modifier.translations,
				},
			});
			dispatch({
				type: EditModifierActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						filePath: response.data.modifier.filePath,
						selectedCustomer: response.data.modifier.customerId,
						totalPrices: response.data.modifier.restaurantsModifiers.map((el) => ({
							restaurantId: el.restaurantId,
							restaurantName: el.restaurantName,
							restaurantStatus: el.restaurantStatus,
							price: String(el.price),
						})),
						IDsIsForceDisabledRestaurant: response.data.modifier.restaurantsModifiers
							.filter((el) => el.forceDisable)
							.map((el) => el.restaurantId),
						selectedModifierGroups: response.data.modifier.modifierGroups.map((el) => el.id),
						name: response.data.modifier.name,
						description: response.data.modifier.description,
						externalId: response.data.modifier.externalId || '',
						isDisabledInRestaurants: response.data.modifier.restaurantsModifiers
							.filter((el) => el.isDisabled)
							.map((el) => el.restaurantId),
						isRestrictedInRestaurants: response.data.modifier.restaurantsModifiers
							.filter((el) => el.isRestricted)
							.map((el) => el.restaurantId),
						isPreselectedInRestaurants: response.data.modifier.restaurantsModifiers
							.filter((el) => el.isPreselected)
							.map((el) => el.restaurantId),
						quantityRegulationRestaurants: response.data.modifier.restaurantsModifiers
							.filter((el) => el.quantityMaxLimit)
							.map((el) => el.restaurantId),
						quantityMaxLimit: maxNumber,
						activationSchedule: response.data.modifier.activationCron,
						deactivationSchedule: response.data.modifier.deactivationCron,
						translations: response.data.modifier.translations,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditModifierActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditModifierVisibleForm) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditModifierActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditModifierActionTypes.DELETE_PHOTO }), []);

	const setCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditModifierActionTypes.SET_CUSTOMER,
			payload: {
				customer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfoWithStatus[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setRestaurantsForPrice = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_RESTAURANTS_FOR_PRICE,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const addPrices = useCallback((value: ITotalPrice[]) => {
		dispatch({
			type: EditModifierActionTypes.ADD_PRICE,
			payload: {
				prices: value,
			},
		});
	}, []);

	const removePrice = useCallback(
		(value: string[]) => {
			const updatedTotalPrices = state.totalPrices.filter((el) => !value.includes(el.restaurantId));
			const updatedRestaurantsForPrices = state.restaurantsForPrice.filter((el) => !value.includes(el));
			const updatedIsDisabledInRestaurants = state.isDisabledInRestaurants.filter((el) => !value.includes(el));
			const updatedIsRestrictedInRestaurants = state.isRestrictedInRestaurants.filter(
				(el) => !value.includes(el),
			);
			const updatedIsPreselectedInRestaurants = state.isPreselectedInRestaurants.filter(
				(el) => !value.includes(el),
			);
			const updatedQuantityRegulationRestaurants = state.quantityRegulationRestaurants.filter(
				(el) => !value.includes(el),
			);
			const updatedIDsIsForceDisabledRestaurant = state.IDsIsForceDisabledRestaurant.filter(
				(el) => !value.includes(el),
			);
			dispatch({
				type: EditModifierActionTypes.REMOVE_PRICE,
				payload: {
					updatedTotalPrices,
					updatedRestaurantsForPrices,
					updatedIsDisabledInRestaurants,
					updatedIsRestrictedInRestaurants,
					updatedIsPreselectedInRestaurants,
					updatedQuantityRegulationRestaurants,
					updatedIDsIsForceDisabledRestaurant,
				},
			});
		},
		[
			state.totalPrices,
			state.restaurantsForPrice,
			state.isDisabledInRestaurants,
			state.isRestrictedInRestaurants,
			state.isPreselectedInRestaurants,
			state.quantityRegulationRestaurants,
			state.IDsIsForceDisabledRestaurant,
		],
	);

	const setIDsIsForceDisabledRestaurant = useCallback(
		(value: string) => {
			if (state.IDsIsForceDisabledRestaurant.includes(value)) {
				const filteredIDsIsForceDisabledRestaurant = state.IDsIsForceDisabledRestaurant.filter(
					(el) => el !== value,
				);
				dispatch({
					type: EditModifierActionTypes.SET_IDS_IS_FORCE_DISABLED_RESTAURANT,
					payload: {
						IDsIsForceDisabledRestaurant: filteredIDsIsForceDisabledRestaurant,
					},
				});
			} else {
				dispatch({
					type: EditModifierActionTypes.SET_IDS_IS_FORCE_DISABLED_RESTAURANT,
					payload: {
						IDsIsForceDisabledRestaurant: [...state.IDsIsForceDisabledRestaurant, value],
					},
				});
			}
		},
		[state.IDsIsForceDisabledRestaurant],
	);

	const setFetchedModifierGroups = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_FETCHED_MODIFIER_GROUPS,
			payload: {
				modifierGroups: value,
			},
		});
	}, []);

	const setModifierGroups = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_MODIFIER_GROUPS,
			payload: {
				modifierGroups: value,
			},
		});
	}, []);

	const updateModifierGroups = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfModifierGroupsShortInfo = value.map((el) => el.id);
			const updatedSelectedModifierGroups = state.selectedModifierGroups.filter((el) =>
				IDsOfModifierGroupsShortInfo.includes(el),
			);

			dispatch({
				type: EditModifierActionTypes.SET_MODIFIER_GROUPS,
				payload: {
					modifierGroups: updatedSelectedModifierGroups,
				},
			});
		},
		[state.selectedModifierGroups],
	);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = Date.now().toString();
		dispatch({
			type: EditModifierActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const setDisabledInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_IS_DISABLED_IN_RESTAURANTS,
			payload: {
				disabledInRestaurants: value,
			},
		});
	}, []);

	const setRestrictedInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_IS_RESTRICTED_IN_RESTAURANTS,
			payload: {
				restrictedInRestaurants: value,
			},
		});
	}, []);

	const setPreselectedInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_IS_PRESELECTED_IN_RESTAURANTS,
			payload: {
				preselectedInRestaurants: value,
			},
		});
	}, []);

	const setQuantityRegulationRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditModifierActionTypes.SET_QUANTITY_REGULATION_RESTAURANTS,
			payload: {
				quantityRegulationRestaurants: value,
			},
		});
	}, []);

	const changeQuantityMaxLimit = useCallback((value: number) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_QUANTITY_MAX_LIMIT,
			payload: {
				quantityMaxLimit: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditModifierActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditModifierActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: EditModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value || null,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value || null,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IUpdateModifier | null) => {
		dispatch({
			type: EditModifierActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditModifierModalWindow) => {
		dispatch({
			type: EditModifierActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<ICreateModifierValidationError> | null) => {
		dispatch({
			type: EditModifierActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => {
		dispatch({
			type: EditModifierActionTypes.RESET_FIELDS,
		});
	}, []);

	return (
		<EditModifierContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				setFileId,
				deletePhoto,
				setCustomer,
				setFetchedRestaurants,
				setRestaurantsForPrice,
				changePrice,
				addPrices,
				removePrice,
				setIDsIsForceDisabledRestaurant,
				setFetchedModifierGroups,
				setModifierGroups,
				updateModifierGroups,
				changeName,
				changeExternalId,
				generateUniqueExternalId,
				changeDescription,
				setDisabledInRestaurants,
				setRestrictedInRestaurants,
				setPreselectedInRestaurants,
				setQuantityRegulationRestaurants,
				changeQuantityMaxLimit,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				modifierId: state.modifierId,
				filePath: state.filePath,
				preSignedUrl: state.preSignedUrl,
				fileId: state.fileId,
				visibleForm: state.visibleForm,
				selectedCustomer: state.selectedCustomer,
				fetchedModifierGroups: state.fetchedModifierGroups,
				selectedModifierGroups: state.selectedModifierGroups,
				name: state.name,
				articleNumber: state.articleNumber,
				description: state.description,
				externalId: state.externalId,
				fetchedRestaurants: state.fetchedRestaurants,
				restaurantsForPrice: state.restaurantsForPrice,
				price: state.price,
				totalPrices: state.totalPrices,
				IDsIsForceDisabledRestaurant: state.IDsIsForceDisabledRestaurant,
				isDisabledInRestaurants: state.isDisabledInRestaurants,
				isRestrictedInRestaurants: state.isRestrictedInRestaurants,
				isPreselectedInRestaurants: state.isPreselectedInRestaurants,
				quantityRegulationRestaurants: state.quantityRegulationRestaurants,
				quantityMaxLimit: state.quantityMaxLimit,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditModifierContext.Provider>
	);
};

export default EditModifierState;
